import { CDN_BASE_URL } from "shared";

// ---------------------------------------------------------------------------
// Public constants
// ---------------------------------------------------------------------------

/**
 * Base CDN URL used to load Monaco scripts and styles.
 */
export const CDN = `${CDN_BASE_URL}/core/monaco/0.55.1`;

// ---------------------------------------------------------------------------
// Internal typings for AMD loader (minimal subset)
// ---------------------------------------------------------------------------

/**
 * Minimal shape of the AMD `require` function exposed by Monaco's loader.
 */
export interface MonacoAmdRequire {
  (deps: string[], callback: (...modules: unknown[]) => void): void;
  config: (cfg: { paths: Record<string, string> }) => void;
}

/**
 * Globally shared boot state for Monaco and its AMD loader.
 */
interface MonacoBootState {
  /** Captured AMD `require` function from loader.js. */
  amdRequire: MonacoAmdRequire | null;
  /** Promise that resolves when the AMD loader has been loaded and captured. */
  amdLoaderReady: Promise<void> | null;
  /** Promise that resolves when `vs/editor/editor.main` has finished loading. */
  monacoReady: Promise<void> | null;
  /** Cached CSS text of `editor.main.css`. */
  cssText: string | null;
  /** Promise that resolves with the CSS text once fetched. */
  cssPromise: Promise<string> | null;
  /**
   * Parsed Constructable StyleSheet for Monaco CSS — shared across all shadow
   * roots. Null when the browser does not support the API.
   */
  cssSheet: CSSStyleSheet | null;
}

/**
 * Typed, globally shared Monaco boot bucket on `globalThis`.
 */
const g: MonacoBootState = ((globalThis as any).__MONACO_BOOT__ ??= {
  amdRequire: null,
  amdLoaderReady: null,
  monacoReady: null,
  cssText: null,
  cssPromise: null,
  cssSheet: null,
});

// ---------------------------------------------------------------------------
// Load AMD loader (without poisoning global require)
// ---------------------------------------------------------------------------

/**
 * Loads Monaco's AMD loader (`vs/loader.js`) once and captures the private
 * `require` function into the shared boot state.
 *
 * The global `require` / `requirejs` are removed afterwards to avoid
 * interfering with other libraries or SPA environments.
 */
export const loadAmdPrivate = (): Promise<void> => {
  // Existing load?
  if (g.amdLoaderReady) return g.amdLoaderReady;

  g.amdLoaderReady = new Promise<void>((resolve, reject) => {
    // If require was already captured
    if (g.amdRequire) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `${CDN}/min/vs/loader.js`;
    script.async = true;

    script.addEventListener("load", () => {
      const w = globalThis as any;
      const localRequire = w.require as MonacoAmdRequire | undefined;

      if (!localRequire || typeof localRequire.config !== "function") {
        reject(new Error("Monaco AMD loader failed to initialize"));
        return;
      }

      g.amdRequire = localRequire;

      // Remove globals so they don't break other libs or SPA environments
      delete w.require;
      delete w.requirejs;

      resolve();
    });

    script.addEventListener("error", () => reject(new Error("Failed to load Monaco AMD loader script")));
    document.head.append(script);
  });

  return g.amdLoaderReady;
};

// ---------------------------------------------------------------------------
// Load Monaco itself
// ---------------------------------------------------------------------------

/**
 * Loads the Monaco editor runtime via the captured AMD loader.
 *
 * Ensures:
 * - AMD loader is initialized (via {@link loadAmdPrivate})
 * - `vs/editor/editor.main` is loaded once
 * - `MonacoEnvironment` is configured to point workers to the CDN
 */
export const loadMonaco = (): Promise<void> => {
  if (g.monacoReady) return g.monacoReady;

  g.monacoReady = loadAmdPrivate().then(() => {
    if (!g.amdRequire) {
      throw new Error("Monaco AMD loader did not initialize correctly.");
    }

    const amd = g.amdRequire;

    amd.config({
      paths: { vs: `${CDN}/min/vs` },
    });

    // Tell Monaco where workers live
    (globalThis as any).MonacoEnvironment = {
      getWorkerUrl() {
        return `${CDN}/min/vs/workers-DcJshg-q.js`;
      },
    };

    // Load editor.main via AMD
    return new Promise<void>((resolve) => {
      amd(["vs/editor/editor.main"], () => {
        // Disable all diagnostics so editors never show error squiggles
        const m = (globalThis as any).monaco;
        const noValidation = { noSemanticValidation: true, noSyntaxValidation: true, noSuggestionDiagnostics: true };
        m?.languages?.typescript?.typescriptDefaults?.setDiagnosticsOptions(noValidation);
        m?.languages?.typescript?.javascriptDefaults?.setDiagnosticsOptions(noValidation);
        resolve();
      });
    });
  });

  return g.monacoReady;
};

// ---------------------------------------------------------------------------
// Load Monaco CSS into a given shadow root (fetch once, inject many)
// ---------------------------------------------------------------------------

/**
 * Ensures Monaco's `editor.main.css` is available inside the given shadow root.
 *
 * The CSS file is fetched once globally and then injected as a `<style
 * data-monaco-css>` element into each shadow root that needs it.
 *
 * @param shadow - Shadow root that hosts the editor container.
 */
export const loadMonacoCss = async (shadow: ShadowRoot): Promise<void> => {
  // Fetch once, shared globally. Build a CSSStyleSheet when supported so
  // each shadow root adopts the same parsed sheet by reference instead of
  // injecting a fresh <style> clone containing ~80 KB of text.
  g.cssPromise ??= fetch(`${CDN}/min/vs/editor/editor.main.css`)
    .then((r) => r.text())
    .then((css) => {
      g.cssText = css;
      try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        g.cssSheet = sheet;
      } catch {
        // Browser does not support Constructable Stylesheets — fall back to <style>.
      }
      return css;
    });

  await g.cssPromise;

  if (g.cssSheet) {
    // adoptedStyleSheets is a FrozenArray; replace it only when the sheet is
    // not already present (reference equality is reliable here).
    if (!shadow.adoptedStyleSheets.includes(g.cssSheet)) {
      shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, g.cssSheet];
    }
    return;
  }

  // Fallback: inject <style> element (parsed separately per shadow root).
  if (shadow.querySelector("style[data-monaco-css]")) return;
  const style = document.createElement("style");
  style.dataset.monacoCss = "true";
  style.textContent = g.cssText!;
  shadow.append(style);
};

// ---------------------------------------------------------------------------
// Pre-warm: start loading Monaco as soon as the bundle is imported, during
// browser idle time. By the time the user mounts an <e-editor>, the AMD
// loader and editor.main bundle are likely already fetched and parsed.
// No-ops in non-browser environments (Node / test runner).
// ---------------------------------------------------------------------------
if (globalThis.requestIdleCallback !== undefined) {
  globalThis.requestIdleCallback(() => void loadMonaco(), { timeout: 5000 });
}
